import { useCallback, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { usePersonalizationTracker, BridgeApi } from '@skin-seoul/utils';
import { useUserLoggedInKey, WP_JUST_LOGGED_OUT_KEY } from '@skin-seoul/service';

/**
 * Bridge component that watches login state changes and triggers
 * personalization re-resolution for the shortcode context.
 *
 * Also detects WordPress logout via `ss_just_logged_out` cookie:
 * clears actorId/sessionId and re-resolves with a fresh anonymous identity.
 *
 * In WebView mode, native handles re-resolve via BridgeWebView,
 * so this component skips re-initialization.
 */
export function ShortcodePersonalizationAuthSync() {
  const client = usePersonalizationTracker();
  const { isLoggedIn } = useUserLoggedInKey();
  const prevIsLoggedInRef = useRef(isLoggedIn);
  const hasInitializedOnceRef = useRef(false);

  const consumeLogoutCookie = useCallback(() => {
    const hasLogoutCookie = Boolean(Cookies.get(WP_JUST_LOGGED_OUT_KEY));
    if (!hasLogoutCookie) {
      return false;
    }

    Cookies.remove(WP_JUST_LOGGED_OUT_KEY);

    prevIsLoggedInRef.current = isLoggedIn;
    client.handleExternalLogout().catch(console.error);

    return true;
  }, [client, isLoggedIn]);

  const initializeIfNeeded = useCallback(() => {
    if (consumeLogoutCookie()) {
      return;
    }

    client.initialize().catch(console.error);
  }, [client, consumeLogoutCookie]);

  const handleAuthStateChange = useCallback(() => {
    if (consumeLogoutCookie()) {
      return;
    }

    if (prevIsLoggedInRef.current === isLoggedIn) {
      return;
    }

    prevIsLoggedInRef.current = isLoggedIn;

    if (isLoggedIn) {
      client.reInitialize().catch(console.error);
      return;
    }

    client.handleExternalLogout().catch(console.error);
  }, [client, consumeLogoutCookie, isLoggedIn]);

  useEffect(() => {
    if (BridgeApi.isWebview()) {
      return;
    }

    if (!hasInitializedOnceRef.current) {
      hasInitializedOnceRef.current = true;
      initializeIfNeeded();
      return;
    }

    handleAuthStateChange();
  }, [initializeIfNeeded, handleAuthStateChange]);

  return null;
}
